import { useDispatch, useSelector } from 'react-redux'
import {
  reviewPaper,
  setActiveFeedbackTab,
  setPaperTitle,
  notifyToast,
} from '@chegg-wt/core-wa/state/actions'
import {
  isPaperCheckInProgressSelector,
  paperIdSelector,
  paperRequiresRecheckSelector,
  paperTitleSelector,
  sentenceOffsetsSelector,
  wordCountSelector,
} from '@chegg-wt/core-wa/state/selectors'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import { Button, IconButton } from '@chegg-ui/button'
import {
  EDITOR_HEADER_TEST_ID,
  FeedbackTab,
  REVIEW,
  UNTITLED,
} from '@chegg-wt/core-wa/constants/paraphraser'
import { CopyIcon } from '@chegg-ui/horizon-icons'
import { colors } from '@chegg-ui/theme'
import { readPaper, updatePaper } from '@chegg-wt/core-wa/utils/paperIndexedDb'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { ToastType } from '@chegg-wt/core-wa/state/types'
import { loggedInSelector } from '@chegg-wt/core/state/user/selectors'
import { AIStarIcon } from '../icons/AIStarIcon'
import { ReviewInfoTooltip } from '../InfoTooltip'
import {
  CopyButton,
  EditorHeaderContainer,
  ReviewButtonContainer,
  TitleContainer,
  TitleInput,
} from './styled'
import { TitleIcon } from './TitleIcon'
import { EditorHeaderProps } from './types'

export const EditorHeader = ({ editor }: EditorHeaderProps) => {
  const routes = useRoutesContext()

  const paperId = useSelector(paperIdSelector)

  const editorTextWordCount = useSelector(wordCountSelector)
  const dispatch = useDispatch()
  const isPaperCheckInProgress = useSelector(isPaperCheckInProgressSelector)
  const title = useSelector(paperTitleSelector)
  const requiresRecheck = useSelector(paperRequiresRecheckSelector)
  const isEmptyTextEditor = editorTextWordCount === 0
  const isAnalysingPaper = editorTextWordCount > 0 && isPaperCheckInProgress
  const sentenceOffset = useSelector(sentenceOffsetsSelector)
  const isLoggedIn = useSelector(loggedInSelector)
  const isPaperAnalysed =
    editorTextWordCount > 0 && !isPaperCheckInProgress && !requiresRecheck

  const copyToClipboard = () => {
    if (editor) {
      const editorText = editor.getText()
      navigator.clipboard
        .writeText(editorText)
        .then(() => {
          // add text copied successfully alert
          dispatch(notifyToast(ToastType.COPY))
        })
        .catch((error) => {
          throw new Error(`Failed to copy text:${error} `)
        })
    }
    if (!editor) {
      throw new Error(`Editor not initiated`)
    }
  }

  const handleReviewClick = () => {
    if (isAnalysingPaper || isPaperAnalysed) return
    dispatch(
      userClick({
        origin: origins.proofReaderReviewClick,
        value: editorTextWordCount.toString(),
      })
    )
    dispatch(reviewPaper({ routes }))
    dispatch(setActiveFeedbackTab(FeedbackTab.GRAMMAR_TAB))
  }

  const handleOnBlurTitle = async () => {
    if (paperId) {
      // TODO: For LoggedIn users, Maintain title change from state instead of updating DB for each keyStroke.
      // Passing isLoggedIn is to decide DB to be updated (indexedDb or cloud DB)
      const paperFromDb = await readPaper(paperId, isLoggedIn)
      if (paperFromDb?.title !== title) updatePaper(paperFromDb.id, { title }, isLoggedIn)
    }
  }

  return (
    <EditorHeaderContainer data-test={`${EDITOR_HEADER_TEST_ID}`}>
      <TitleContainer>
        <TitleIcon />
        <TitleInput
          placeholder={UNTITLED}
          value={title}
          labelText=""
          onChange={(value) => dispatch(setPaperTitle(value))}
          onBlur={handleOnBlurTitle}
          inputId="title-input"
        />
      </TitleContainer>
      <ReviewButtonContainer>
        {isPaperAnalysed ? (
          <IconButton
            size="medium"
            screenReaderOnlyText="Review"
            data-test="header-collapsed-review-button"
            disableHoverAnimation
            variant="secondary"
            disabled
          >
            <AIStarIcon color={colors.grey300} />
          </IconButton>
        ) : (
          <>
            <Button
              size="medium"
              leadingIcon={
                !isAnalysingPaper && (
                  <AIStarIcon color={isEmptyTextEditor ? colors.grey600 : colors.white} />
                )
              }
              onClick={handleReviewClick}
              isLoading={isAnalysingPaper}
              loadingText={REVIEW}
              disableHoverAnimation
              disabled={isEmptyTextEditor || !requiresRecheck}
              data-test="header-review-button"
            >
              {REVIEW}
            </Button>
            {sentenceOffset.length > 0 && !isPaperCheckInProgress && requiresRecheck && (
              <ReviewInfoTooltip />
            )}
          </>
        )}
        <CopyButton
          screenReaderOnlyText="Copy Paper"
          onClick={copyToClipboard}
          data-test="header-copy-button"
          disabled={isEmptyTextEditor}
        >
          <CopyIcon />
        </CopyButton>
      </ReviewButtonContainer>
    </EditorHeaderContainer>
  )
}

export default EditorHeader
